<template>
    <section class="tk-workspace-header">
        <div class="tk-workspace-header__identity">
            <q-avatar class="tk-workspace-header__avatar" size="50px">
                {{ initials }}
            </q-avatar>
            <div>
                <h1>{{ engagement.personName }}</h1>
                <div class="tk-workspace-header__badges">
                    <TkContextBadge kind="status" :value="engagement.status_code" :label="engagement.statusLabel" />
                    <TkContextBadge kind="needs" :value="engagement.needs_tier || ''" :label="engagement.needsTierLabel" />
                    <span>{{ engagement.service_name || "-" }}</span>
                </div>
            </div>
        </div>


        <div v-if="riskLabels || engagement.activeReminder" class="tk-workspace-header__alerts">
            <div v-if="riskLabels" class="tk-workspace-header__alert tk-workspace-header__alert--risk">
                <q-icon name="warning_amber" />
                <strong>Current risk: {{ riskLabels }}</strong>
                <span>— identified {{ engagement.riskAssessmentAt }}</span>
            </div>
            <div v-if="engagement.activeReminder" class="tk-workspace-header__alert tk-workspace-header__alert--review">
                <q-icon name="o_notifications" />
                <strong>{{ engagement.activeReminder.isDue ? "Review due" : "Next review" }}</strong>
                <span>— {{ engagement.activeReminder.dueLabel }}</span>
            </div>
        </div>

        <dl class="tk-workspace-header__facts">
            <div>
                <dt>Time spent</dt>
                <dd>{{ engagement.timeSpent }}</dd>
            </div>
            <div>
                <dt>Primary Pou</dt>
                <dd>
                    <TkContextBadge
                        v-if="engagement.primaryWellbeingTitle !== '-'"
                        kind="pou"
                        :value="engagement.primaryWellbeingTitle"
                        :label="engagement.primaryWellbeingTitle"
                    />
                    <span v-else>-</span>
                </dd>
            </div>
            <div>
                <dt>Last contact</dt>
                <dd>{{ engagement.lastContactAt }}</dd>
            </div>
            <div>
                <dt>Entry date</dt>
                <dd>{{ engagement.enteredAt }}</dd>
            </div>
            <div>
                <dt>Last updated</dt>
                <dd>{{ lastUpdated }}</dd>
            </div>
        </dl>
    </section>
</template>

<script>
import TkContextBadge from "@/components/TkClients/TkContextBadge.vue";
import { personFullName } from "@/services/util/tkClients";

const personInitials = (value) => {
    const parts = String(value || "").trim().split(/\s+/).filter(Boolean);
    return parts.slice(0, 2).map(part => part[0]).join("").toUpperCase() || "-";
};

export default {
    name: "TkEngagementWorkspaceHeader",
    components: {
        TkContextBadge,
    },
    props: {
        engagement: {
            type: Object,
            required: true,
        },
    },
    computed: {
        initials() {
            return personInitials(personFullName(this.engagement) || this.engagement.preferred_name);
        },
        lastUpdated() {
            const worker = this.engagement.updated_by_name;
            return worker ? `${this.engagement.updatedAt}, by ${worker}` : this.engagement.updatedAt;
        },
        riskLabels() {
            return (this.engagement.risk_assessments || []).map(risk => risk.label).join(", ");
        },
    },
};
</script>

<style>
.tk-workspace-header {
    padding: 18px 24px 14px;
    border-bottom: 1px solid rgba(47, 62, 70, 0.1);
    background: white;
}

.tk-workspace-header__identity {
    display: flex;
    align-items: center;
    gap: 12px;
}

.tk-workspace-header__avatar {
    flex: 0 0 auto;
    background: #e5eee8;
    color: #244f43;
    font-weight: 750;
}

.tk-workspace-header h1 {
    margin: 0 0 5px;
    color: #35443e;
    font-size: 1.3rem;
    font-weight: 600;
    letter-spacing: 0.025em;
    line-height: 1.15;
}

.tk-workspace-header__badges {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 7px;
    color: #647169;
    font-size: 0.78rem;
}

.tk-workspace-header__facts {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 14px 32px;
    max-width: 920px;
    margin: 18px 0 0;
}

.tk-workspace-header__facts div {
    min-width: 0;
}

.tk-workspace-header__facts dt {
    margin-bottom: 3px;
    color: #78837d;
    font-size: 0.68rem;
    font-weight: 500;
    letter-spacing: 0.07em;
    text-transform: uppercase;
}

.tk-workspace-header__facts dd {
    margin: 0;
    color: #3c4b45;
    font-size: 0.86rem;
    font-weight: 550;
    overflow-wrap: anywhere;
}

.tk-workspace-header__alerts {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
    margin-top: 16px;
}

.tk-workspace-header__alert {
    display: flex;
    align-items: center;
    gap: 7px;
    min-width: 0;
    min-height: 38px;
    padding: 8px 12px;
    border: 1px solid;
    border-radius: 8px;
    font-size: 0.78rem;
}

.tk-workspace-header__alert strong {
    flex: 0 1 auto;
}

.tk-workspace-header__alert span {
    color: #6f776f;
    overflow-wrap: anywhere;
}

.tk-workspace-header__alert--risk {
    border-color: #e7ab98;
    background: #f9e6df;
    color: #ad412d;
}

.tk-workspace-header__alert--review {
    border-color: #e3bc7b;
    background: #fbefdc;
    color: #9a611e;
}

@media (max-width: 700px) {
    .tk-workspace-header {
        padding: 14px 12px 12px;
    }

    .tk-workspace-header__facts,
    .tk-workspace-header__alerts {
        grid-template-columns: 1fr;
    }

    .tk-workspace-header__facts {
        gap: 11px;
        margin-top: 14px;
    }

    .tk-workspace-header__alert {
        align-items: flex-start;
        flex-wrap: wrap;
    }
}
</style>
